import * as z from "zod";
import { codeName, flag, linkToken, pagination, uuidV7, version } from "../index";
import { etafOrigins, etafStatuses, legalForms } from "./enums";
import { countryCode, emailAddress, organizationRef, shortText } from "./fields";

// The e-TAF of a customer (50-api/party.md §2.7, ADR-105): the form with its company data, one application per
// customer, a new version of the form at every submission. Tomorrow reviews it, corrects it, approves it or rejects
// it; an approval writes the form into the directory.

const MAX_ETAF_ADDRESSES = 50;
const MAX_ETAF_PARTNERS = 20;
export const MAX_ETAF_REASON = 2000;

/** An address of the address book of the form. Nothing is required until the approval. */
export const etafAddress = z.object({
  /** The address of the directory this entry is, when the form started from it: null for a new one. */
  id: z.uuid().nullable(),
  addressee: shortText(200).nullable(),
  attention: shortText(200).nullable(),
  line1: shortText(200).nullable(),
  building: shortText(60).nullable(),
  floor: shortText(30).nullable(),
  careOf: shortText(100).nullable(),
  city: shortText(100).nullable(),
  postalCode: shortText(20).nullable(),
  stateProvince: shortText(100).nullable(),
  countryCode: countryCode.nullable(),
  phone: shortText(40).nullable(),
  isDefaultBilling: z.boolean(),
  isDefaultShipping: z.boolean(),
});
export type EtafAddress = z.output<typeof etafAddress>;

/** A partner of a partnership. */
export const etafPartner = z.object({
  name: shortText(200),
  addressLine: shortText(200).nullable(),
  city: shortText(100).nullable(),
  postalCode: shortText(20).nullable(),
  stateProvince: shortText(100).nullable(),
  countryCode: countryCode.nullable(),
});
export type EtafPartner = z.output<typeof etafPartner>;

/**
 * The form, whole: what a version of it keeps (`etaf_submission.payload`). Every field is there and can be empty, so
 * a form can be saved before it is complete: what an approval needs is checked by the approval.
 */
export const etafForm = z
  .object({
    legalName: shortText(200).nullable(),
    vatNumber: shortText(40).nullable(),
    /** Codice Destinatario of the Italian electronic invoice. */
    sdiCode: shortText(7).nullable(),
    pecEmail: emailAddress.nullable(),
    email: emailAddress.nullable(),
    phone: shortText(40).nullable(),
    /** The registered office; `countryCode` is its country, and the one of the company. */
    registeredLine1: shortText(200).nullable(),
    registeredCity: shortText(100).nullable(),
    registeredPostalCode: shortText(20).nullable(),
    registeredStateProvince: shortText(100).nullable(),
    countryCode: countryCode.nullable(),
    legalForm: z.enum(legalForms).nullable(),
    legalFormOther: shortText(100).nullable(),
    companyRegNo: shortText(60).nullable(),
    incorporationYear: z.number().int().min(1700).max(2100).nullable(),
    partners: z.array(etafPartner).max(MAX_ETAF_PARTNERS),
    /** Where the customer is invoiced and receives the goods: one default for each. */
    addresses: z.array(etafAddress).max(MAX_ETAF_ADDRESSES),
    accountsPayableFirstName: shortText(100).nullable(),
    accountsPayableLastName: shortText(100).nullable(),
    accountsPayablePhone: shortText(40).nullable(),
    accountsPayableEmail: emailAddress.nullable(),
    /** Who receives the invoices, the statements and the credit memos, when not the accounts payable contact. */
    invoicesEmail: emailAddress.nullable(),
    statementsEmail: emailAddress.nullable(),
    creditMemosEmail: emailAddress.nullable(),
    declarationAccepted: z.boolean(),
    /** Who signs the declaration for the customer. */
    signedBy: shortText(200).nullable(),
    signerPosition: shortText(100).nullable(),
    signedOn: z.iso.date().nullable(),
  })
  .meta({ id: "EtafForm" });
export type EtafForm = z.output<typeof etafForm>;

/** The form with nothing written: where Tomorrow starts, and the customer when the directory has nothing of it. */
export const emptyEtafForm: EtafForm = {
  legalName: null,
  vatNumber: null,
  sdiCode: null,
  pecEmail: null,
  email: null,
  phone: null,
  registeredLine1: null,
  registeredCity: null,
  registeredPostalCode: null,
  registeredStateProvince: null,
  countryCode: null,
  legalForm: null,
  legalFormOther: null,
  companyRegNo: null,
  incorporationYear: null,
  partners: [],
  addresses: [],
  accountsPayableFirstName: null,
  accountsPayableLastName: null,
  accountsPayablePhone: null,
  accountsPayableEmail: null,
  invoicesEmail: null,
  statementsEmail: null,
  creditMemosEmail: null,
  declarationAccepted: false,
  signedBy: null,
  signerPosition: null,
  signedOn: null,
};

/**
 * What the customer no longer changes once Tomorrow approved its e-TAF a first time (Q-PAR-15, ADR-175): who it is for
 * the tax office and for the electronic invoice. Tomorrow still does.
 */
export const etafLockedFields = ["vatNumber", "sdiCode"] as const satisfies readonly (keyof EtafForm)[];
export type EtafLockedField = (typeof etafLockedFields)[number];

const etafFilters = {
  /** Part of the name, of the legal name, of the VAT number or of the code of the customer, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  status: z.enum(etafStatuses).optional(),
  /** Only the ones that wait for Tomorrow to decide. */
  waiting: flag.optional(),
  customerOrgId: z.uuid().optional(),
};

/** The last changed first. */
export const listEtafApplicationsInput = z.object({
  ...etafFilters,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListEtafApplicationsInput = z.input<typeof listEtafApplicationsInput>;

/** How many the list has with the same filters (ADR-086). */
export const countEtafApplicationsInput = z.object(etafFilters);
export type CountEtafApplicationsInput = z.input<typeof countEtafApplicationsInput>;

export const getEtafApplicationInput = z.object({ applicationId: z.uuid() });
export type GetEtafApplicationInput = z.input<typeof getEtafApplicationInput>;

/** The id comes from the client (ADR-046). A customer has one e-TAF. */
export const openEtafApplicationInput = z.object({ id: uuidV7, customerOrgId: z.uuid() });
export type OpenEtafApplicationInput = z.input<typeof openEtafApplicationInput>;

/** The form as Tomorrow writes it, whole: a new version of it, with the version of the application the client read. */
export const amendEtafApplicationInput = z.object({ applicationId: z.uuid(), version, form: etafForm });
export type AmendEtafApplicationInput = z.input<typeof amendEtafApplicationInput>;

/** An action on an e-TAF, with the version the client read. */
export const etafActionInput = z.object({ applicationId: z.uuid(), version });
export type EtafActionInput = z.input<typeof etafActionInput>;

/** The reason is what the customer is told. */
export const rejectEtafApplicationInput = etafActionInput.extend({ reason: shortText(MAX_ETAF_REASON) });
export type RejectEtafApplicationInput = z.input<typeof rejectEtafApplicationInput>;

/** The gateway routes split the input: the id in the path, the rest in the body. */
export const etafParams = etafActionInput.pick({ applicationId: true });
export const etafVersionBody = etafActionInput.pick({ version: true });
export const amendEtafApplicationBody = amendEtafApplicationInput.omit({ applicationId: true });
export const rejectEtafApplicationBody = rejectEtafApplicationInput.omit({ applicationId: true });

/**
 * Tomorrow sends the customer the personal link of its e-TAF (ADR-175), by email, to one of its contacts: a new link
 * replaces the one before.
 */
export const sendEtafLinkInput = etafActionInput.extend({ contactId: z.uuid() });
export type SendEtafLinkInput = z.input<typeof sendEtafLinkInput>;
export const sendEtafLinkBody = sendEtafLinkInput.omit({ applicationId: true });

/** The customer, with the token of its link alone (ADR-175): the form as it is now. */
export const readPublicEtafInput = z.object({ token: linkToken });
export type ReadPublicEtafInput = z.input<typeof readPublicEtafInput>;

/** The customer sends its form, whole, with the token of its link: a new version, which waits for Tomorrow. */
export const submitPublicEtafInput = z.object({ token: linkToken, form: etafForm });
export type SubmitPublicEtafInput = z.input<typeof submitPublicEtafInput>;

export const etafApplicationSummaryDto = z
  .object({
    id: z.uuid(),
    customer: organizationRef,
    countryCode: z.string().nullable().optional(),
    vatNumber: z.string().nullable().optional(),
    status: z.enum(etafStatuses),
    origin: z.enum(etafOrigins),
    /** The version of the form: 0 until something is sent. */
    formVersion: z.number().int(),
    submittedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "EtafApplicationSummary" });

/** One version of the form, without its content. */
const etafVersionDto = z.object({
  version: z.number().int(),
  /** Written by Tomorrow, not by the customer. */
  amended: z.boolean(),
  submittedAt: z.iso.datetime(),
  /** Who wrote it, by name: the user of Tomorrow, or the contact of the customer who sent it from its link. */
  submittedBy: z.string().nullable(),
});

/** The personal link of the e-TAF (ADR-175): who it went to, when, and until when it works. */
const etafLinkDto = z.object({
  contact: z.object({ id: z.uuid(), name: z.string() }),
  /** Null while its email has not left: then the link does not work. */
  sentAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
});

export const etafApplicationDto = z
  .object({
    id: z.uuid(),
    customer: organizationRef,
    status: z.enum(etafStatuses),
    /** `migrated`: approved in the portal of today, without versions of the form in Nexxt (ADR-180). */
    origin: z.enum(etafOrigins),
    formVersion: z.number().int(),
    /** The current version of the form; null until something is sent. */
    form: etafForm.nullable().optional(),
    /** The versions of the form, the current one first. */
    versions: z.array(etafVersionDto),
    /** When the customer last sent the form. */
    submittedAt: z.iso.datetime().nullable(),
    /** The last approval: the directory has the form that was current then. */
    approvedAt: z.iso.datetime().nullable(),
    /** The user of Tomorrow who approved it, by name. */
    approvedBy: z.string().nullable().optional(),
    rejectionReason: z.string().nullable().optional(),
    /** The last link sent to the customer; null if none was. */
    link: etafLinkDto.nullable(),
    updatedAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "EtafApplication" });

/**
 * The e-TAF as its customer reads it from its link (ADR-175): the form as it is now, or as the directory has the
 * customer when nothing was sent yet; what it can no longer change; why Tomorrow rejected it. Nothing of Tomorrow's.
 */
export const publicEtafDto = z
  .object({
    customer: z.object({ name: z.string() }),
    status: z.enum(etafStatuses),
    form: etafForm,
    lockedFields: z.array(z.enum(etafLockedFields)),
    rejectionReason: z.string().nullable(),
    /** When the customer last sent the form. */
    submittedAt: z.iso.datetime().nullable(),
    /** Until when the link works. */
    expiresAt: z.iso.datetime(),
    /** The countries the form offers. */
    countries: z.array(codeName),
  })
  .meta({ id: "PublicEtaf" });
